/**
 * Image fallback map.
 *
 * Every image on the site is served from the local `/uploads/` folder on the
 * VPS. If a local file is missing, the global error listener installed by
 * `installImageFallback()` transparently swaps that image for its original
 * CDN copy, so the page never shows a broken image.
 *
 * Key   -> local path used in the markup (e.g. "/uploads/hero-home.jpeg")
 * Value -> the original CDN URL for the same image.
 */
export const CDN_FALLBACKS: Record<string, string> = {
  // Brand / logo / social
  "/uploads/logo-lahari.png":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/0b3b31cd6a81a00dadd32efb0f200320.png",
  "/uploads/social-cover.jpeg":
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20banner%20of%20a%20professional%20moving%20crew%20in%20uniform%20loading%20neatly%20wrapped%20cardboard%20boxes%20and%20furniture%20into%20a%20clean%20modern%20moving%20truck%20parked%20outside%20a%20bright%20Bangalore%20residential%20apartment%20building%2C%20warm%20golden%20hour%20light%2C%20deep%20green%20and%20cream%20color%20palette%2C%20clear%20uncluttered%20composition%2C%20photorealistic%20premium%20relocation%20marketing%20photography%2C%20no%20text%20letters%20or%20logos&width=1200&height=630&seq=lahari-social-cover-2026&orientation=landscape",

  // Heroes & truck / brand graphics
  "/uploads/hero-home.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/ca67b860c0853f8ccc3e566dcf2278c0.jpeg",
  "/uploads/hero-fleet-banner.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/e234a66ee38cbbb827fb9cae073e3955.jpeg",
  "/uploads/truck-lahari.png":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/b03e8d3f4816b17ab4f21838c745f9ac.png",
  "/uploads/truck-on-road.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/47586adf532104c80e178c1961f59df0.jpeg",
  "/uploads/values-wheel.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/b47c97ab2f4b92441924508e848fc520.jpeg",
  "/uploads/vision-icon.png":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/81cbf806355112afc4f1ab3ef039b0e6.png",
  "/uploads/mission-icon.png":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/7cc7a6f87262d550f4bc6ef186eb01c5.png",

  // About
  "/uploads/about-office-shifting.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/0d0b67edeb56ed35a10dd02d4655714b.jpeg",
  "/uploads/about-house-shifting.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/c6a0e3e3af2a2a24322b7a6e58e00035.jpeg",
  "/uploads/about-storage.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/1cf904f0fdf3033201b990dec374a5e9.jpeg",
  "/uploads/about-hero-truck.png":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/84ba163f9a57c7533a72b99ba68333ef.png",
  "/uploads/about-refrigerator-sofa.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/15e963fc956e19bf340e3f69e3bb93f8.jpeg",

  // Services
  "/uploads/service-house-shifting.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/ebf822cf5f7c3307c02cc4ae535029de.jpeg",
  "/uploads/service-office-shifting.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/3e3afdd9504f8192115b3bc01513c47a.jpeg",
  "/uploads/service-storage.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/35120e4809504da86da1cb2ce92c181e.jpeg",
  "/uploads/service-local-shifting.jpeg":
    "https://readdy.ai/api/search-image?query=Professional%20moving%20crew%20carrying%20cardboard%20boxes%20into%20a%20covered%20moving%20truck%20parked%20outside%20a%20modern%20residential%20apartment%20building%20in%20Bangalore%20India%2C%20bright%20daylight%2C%20clean%20editorial%20photography%2C%20with%20no%20text%2C%20letters%2C%20logos%20or%20brand%20marks%2C%20warm%20neutral%20tones%2C%20high%20detail&width=1200&height=800&seq=local-shifting-bangalore-2026&orientation=landscape&nocache=true",
  // Team / crew / warehouse
  "/uploads/crew-container.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/61bdb8b274095b1c2b0bde9daff92767.jpeg",
  "/uploads/crew-ready.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/15a4026d076c86753b0b78122de61819.jpeg",
  "/uploads/warehouse-office.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/7a632793328b7d30eb2aa72ef373f975.jpeg",

  // Gallery
  "/uploads/gallery-container-loaded.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/f0dbf5a5475e5734a60e210e316792a4.jpeg",
  "/uploads/gallery-garden-plants.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/88c7b1af825bd1e0a0ecbb11debe2802.jpeg",
  "/uploads/gallery-plants-pots.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/0c8df2c9608e63f495b1efbc9f01e3ed.jpeg",
  "/uploads/gallery-wardrobe-wrap.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/9523385eccc847d03ad29e5f34a2de47.jpeg",
  "/uploads/gallery-furniture-mirrors.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/50acb17296175799bfd359c30a46f391.jpeg",
  "/uploads/gallery-goods-secured.jpeg":
    "https://static.readdy.ai/image/d59ba4ba4e5f160e4781d8b81f851394/f805e41e1524db4e7c910702cbef594a.jpeg",

  // Review / testimonial avatars
  "/uploads/reviewer-sujata.jpeg":
    "https://readdy.ai/api/search-image?query=Photorealistic%20friendly%20Indian%20woman%20smiling%20softly%20in%20a%20bright%20indoor%20setting%2C%20natural%20light%20portrait%20for%20a%20customer%20review%20avatar%2C%20clean%20plain%20background%2C%20realistic%20photography&width=240&height=240&seq=lahari-reviewer-1-2026&orientation=squarish",
  "/uploads/reviewer-ankit.jpeg":
    "https://readdy.ai/api/search-image?query=Photorealistic%20friendly%20Indian%20man%20smiling%20in%20a%20casual%20shirt%2C%20natural%20daylight%20portrait%20for%20a%20customer%20review%20avatar%2C%20clean%20plain%20background%2C%20realistic%20photography&width=240&height=240&seq=lahari-reviewer-2-2026&orientation=squarish",
  "/uploads/reviewer-govardhan.jpeg":
    "https://readdy.ai/api/search-image?query=Photorealistic%20cheerful%20Indian%20man%20with%20glasses%20smiling%20warmly%2C%20natural%20light%20portrait%20for%20a%20customer%20review%20avatar%2C%20simple%20neutral%20background%2C%20realistic%20photography&width=240&height=240&seq=lahari-reviewer-3-2026&orientation=squarish",
  "/uploads/reviewer-sheetal.jpeg":
    "https://readdy.ai/api/search-image?query=Photorealistic%20friendly%20South%20Indian%20woman%20in%20her%20early%20thirties%20with%20dark%20wavy%20shoulder%20length%20hair%2C%20soft%20natural%20smile%2C%20casual%20top%2C%20clean%20plain%20light%20background%2C%20realistic%20customer%20profile%20photo%2C%20square%20crop&width=240&height=240&seq=lahari-google-reviewer-sheetal&orientation=squarish",
  "/uploads/reviewer-raju.jpeg":
    "https://readdy.ai/api/search-image?query=Photorealistic%20professional%20South%20Indian%20man%20in%20his%20forties%20wearing%20a%20navy%20blue%20formal%20suit%20with%20light%20shirt%2C%20short%20dark%20hair%2C%20friendly%20confident%20smile%2C%20clean%20plain%20light%20background%2C%20realistic%20business%20profile%20photo%2C%20square%20crop&width=240&height=240&seq=lahari-google-reviewer-raju&orientation=squarish",
};

/** Resolve a CDN fallback for a local "/uploads/..." path, or null when none exists. */
export const getCdnFallback = (src: string): string | null => {
  if (!src) return null;
  let path = src;
  try {
    path = new URL(src, window.location.origin).pathname;
  } catch {
    path = src.split("?")[0].split("#")[0];
  }
  return CDN_FALLBACKS[path] ?? null;
};

let installed = false;

/**
 * Installs a document-level error listener. Whenever any <img> fails to load
 * (for example a missing local file), it is swapped once for its CDN copy.
 * Runs in the capture phase because image error events do not bubble.
 */
export const installImageFallback = (): void => {
  if (installed || typeof document === "undefined") return;
  installed = true;

  document.addEventListener(
    "error",
    (event) => {
      const target = event.target as HTMLImageElement | null;
      if (!target || !(target instanceof HTMLImageElement)) return;

      const current = target.getAttribute("src") || target.currentSrc || target.src;
      if (!current) return;
      // Swap each distinct broken source exactly once. Tracking the failed
      // source (instead of the whole <img> element) means a reused image node
      // — such as the gallery lightbox when navigating between photos — can
      // still fall back to its own CDN copy.
      if (target.dataset.cdnFallbackSrc === current) return;

      const fallback = getCdnFallback(current);
      if (!fallback || fallback === current) return;

      target.dataset.cdnFallback = "1";
      target.dataset.cdnFallbackSrc = current;
      target.src = fallback;
    },
    true,
  );
};